import React, { useMemo } from "react";
import T from "prop-types";
import Flexbox from "commons/components/Flexbox";
import Typography from "commons/components/Typography";
import Button from "commons/components/Button";
import { useKeyPress } from "commons/util/useKeyPress";
import SanitizedHtml from "commons/components/SanitizedHtml";
import { useTimer } from "commons/util/useTimer";
import { Marker, Step } from "App/Recipes/components/Recipes.styles";
import { useTranslation } from "react-i18next";

function Steps({ className, steps, activeStepIndex, setActiveStepIndex }) {
  const { t } = useTranslation();
  const { showTimer } = useTimer();

  const canGoPrevious = useMemo(() => {
    return activeStepIndex !== 0;
  }, [activeStepIndex]);

  const canGoNext = useMemo(() => {
    return activeStepIndex !== steps.length - 1;
  }, [activeStepIndex]);

  function goPrevious() {
    setActiveStepIndex(activeStepIndex - 1);
  }

  function goNext() {
    setActiveStepIndex(activeStepIndex + 1);
  }

  useKeyPress("ArrowLeft", () => {
    if (canGoPrevious) goPrevious();
  });
  useKeyPress("ArrowUp", () => {
    if (canGoPrevious) goPrevious();
  });
  useKeyPress("ArrowRight", () => {
    if (canGoNext) goNext();
  });
  useKeyPress("ArrowDown", () => {
    if (canGoNext) goNext();
  });

  function onStepClick(event, stepIndex) {
    if (event.target.className === "timer") {
      showTimer(Number(event.target.value), stepIndex);
    } else if (stepIndex !== activeStepIndex) {
      setActiveStepIndex(stepIndex);
    }
  }

  return (
    <Flexbox className={className} gap={24} flexDirection="column" flexGrow={1}>
      <Flexbox alignItems="center" justifyContent="space-between">
        <Typography variant="h3">{t("Recipes.Directions")}</Typography>
        <Flexbox gap={8}>
          <Button onClick={goPrevious} variant="tertiary" icon="arrow_back" disabled={!canGoPrevious} />
          <Button onClick={goNext} variant="tertiary" icon="arrow_forward" disabled={!canGoNext} />
        </Flexbox>
      </Flexbox>
      <Flexbox flexDirection="column" justifyContent="center" gap={24} flexGrow={1}>
        {steps.map((step, i) => (
          <Step
            key={i}
            alignItems="flex-start"
            gap={16}
            isHighlighted={i === activeStepIndex}
            onClick={event => onStepClick(event, i)}
          >
            <Marker>{i + 1}.</Marker>
            <Typography variant="paragraph" as="div">
              <SanitizedHtml>{step.text}</SanitizedHtml>
            </Typography>
          </Step>
        ))}
      </Flexbox>
    </Flexbox>
  );
}

Steps.propTypes = {
  className: T.string,
  steps: T.arrayOf(T.object),
  activeStepIndex: T.number,
  setActiveStepIndex: T.func,
};

export default Steps;
